/**
 * THE TWO BOTTOM-RIGHT CORNER MOUNTS, AND THE ONE RULE THEY SHARE.
 *
 * The customer-facing FroggyAI pill and the staff FroggerAI dock are both
 * `position: fixed` in the bottom-right corner, and the pill is the
 * HIGHER layer (`--z-modal` 1095 vs `--ff-z-frogger-dock` 1040). A page
 * that renders both puts the customer's pill exactly on top of the
 * keeper's MIC and SEND buttons. Found by the real-browser harness at
 * 1440 (S3-06.3c): a Playwright TAP goes through hit-testing and
 * reported "froggy-bubble-btn intercepts pointer events", which is
 * precisely what a wet thumb at a rack would have discovered.
 *
 * So they live in ONE FILE, deciding from ONE predicate. Two regexes in
 * two components are two chances to drift apart, and drift here is
 * silent — nothing throws, nothing logs, a control just stops working.
 *
 * They are still RENDERED from `App.js`, as siblings of
 * `<main class="app-content">`: `.app-content` is a flex item at
 * `z-index: 1 !important` and therefore a stacking context nothing
 * inside can escape. `styles/__tests__/dialogLayers.test.js` pins that
 * mount site.
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSignedInUserId } from '../../context/AuthContext';
import { userPart } from '../../utils/froggerStorageKeys';
import FroggyChatBubble from '../FroggyChatBubble/FroggyChatBubble';
import FroggerDock from '../froggerAI/FroggerDock';
import { froggerDockFacilityId } from './dockRoutes';

// THE ONE PREDICATE lives in `./dockRoutes` (the navbar's FroggerAI
// button reads it too) and is re-exported here for its existing callers.
// Since Fable ruling R4 (2026-10-07) it answers for `/my-store/<id>/…`
// as well as `/facilities/<id>/…`.
export { froggerDockFacilityId } from './dockRoutes';

/**
 * Route-aware mount for the global FroggyChat bubble. Hidden on routes
 * where the bubble would distract from the primary intent of the page or
 * duplicate existing UI:
 *
 *   - /checkout/*           — don't sidetrack a purchase in flight
 *   - /account/froggy-chat* — full-page chat already on screen
 *   - /internal/*           — admin/telemetry tools; not customer surfaces
 *   - /login, /signup, /reset-password*, /email-* — auth flows; the
 *     visitor is here to authenticate, not chat
 *   - /email-preferences/:token — single-purpose token-gated page;
 *     a chat side-channel here is just confusing
 *   - /dev/*                — engineering routes; chat surface unwanted
 *
 * (P-06, 2026-05-25; expanded per adversarial review MED-3.)
 */
export function FroggyChatBubbleMount() {
  const { pathname } = useLocation();
  const p = (pathname || '').toLowerCase();
  const hideOn = (
    p.startsWith('/checkout/')
    || p.startsWith('/account/froggy-chat')
    || p.startsWith('/internal/')
    || p.startsWith('/login')
    || p.startsWith('/signup')
    || p.startsWith('/reset-password')
    || p.startsWith('/email-confirmation-sent')
    || p.startsWith('/email-verified')
    || p.startsWith('/email-preferences')
    || p.startsWith('/dev/')
    // P-11 marketing chat surface — the operator has their own AI
    // chat here; surfacing the customer-facing FroggyAI pill on top
    // would put two AI chats on the same screen and partially overlap
    // the assistant message bubbles.
    || /^\/my-store\/[^/]+\/marketing\/chat/.test(p)
    // I-03 R6 · S3-06.3 — EVERY FACILITY PAGE, for the same reason as
    // the marketing chat above and one more: the pill is a SECOND AI
    // chat on the keeper's screen AND it physically covers the dock's
    // controls. The keeper is staff standing at a rack; the customer
    // advisor is not what they are reaching for. Since R4 (2026-10-07)
    // that is every MY-STORE page too — the console is one console.
    || froggerDockFacilityId(pathname) !== null
  );
  if (hideOn) return null;
  return <FroggyChatBubble />;
}

/**
 * I-03 R6 · S3-06.3 — the FroggerAI dock, on EVERY facility page (and,
 * since Fable ruling R4 of 2026-10-07, every `/my-store/<id>/…` page).
 *
 * There is no facility LAYOUT route to hang it from: the facility
 * section is a flat list of `/facilities/:facilityId/<section>` routes,
 * not a nested `<Route element={<FacilityShell/>}>`. Converting them
 * would be a router rewrite in a sprint that is not about routing, so
 * the dock is mounted once and reads the facility id off the path.
 */
export function FroggerDockMount() {
  const { pathname } = useLocation();
  const userId = useSignedInUserId();
  const facilityId = froggerDockFacilityId(pathname);
  if (!facilityId) return null;
  // ⚠️ `key` IS LOAD-BEARING (S3-06.4 review). The dock is mounted once
  // for the whole app and only its PROP changes when the operator walks
  // from facility A to facility B — so React keeps the same instance,
  // and with it A's transcript, A's conversation id and A's undo
  // tokens, now sitting on B's screen and persisted under B's
  // sessionStorage key. Keying on the facility makes the walk a
  // remount, which is what "a different facility is a different
  // conversation" means. And the SIGNED-IN USER is in the key too
  // (R-TRAINING junior fix 1): a different person is a different
  // conversation, so a sign-in as somebody else is a remount, never the
  // last user's transcript kept in memory.
  return <FroggerDock key={`${userPart(userId)}|${facilityId}`} facilityId={facilityId} />;
}
